'use client';

import type React from 'react';
import { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FieldHelp, useAutoStartTour } from '@/features/guided-help';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { DollarSignIcon, Upload } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import MovementsTable, {
  type TypeFilter,
} from '@/components/manager-balance/MovementsTable';
import CancelDebtDrawer, {
  type DrawerMode,
} from '@/components/manager-balance/CancelDebtDrawer';
import PendingTransfersBanner from '@/components/manager-balance/PendingTransfersBanner';
import { useCurrentAccount } from '@/hooks/useCurrentAccount';
import type { DebtPaymentRequest } from '@/types/manager.model';
import { currencyFormatter } from '@/utils/movements-utils';

const CurrentAccount: React.FC = () => {
  const {
    negativeBalance,
    maxBalance,
    movements,
    debtPayments,
    pendingRequests,
    isLoading,
    error,
    refetch,
  } = useCurrentAccount();

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('create');
  const [selectedRequest, setSelectedRequest] =
    useState<DebtPaymentRequest | null>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  useAutoStartTour('panel.current-account');

  const hasDebt = negativeBalance > 0;

  const openCreate = () => {
    setSelectedRequest(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  };

  const openDetail = (requestId: string) => {
    const request = debtPayments.find((req) => req.id === requestId);
    if (!request) return;
    setSelectedRequest(request);
    setDrawerMode('detail');
    setDrawerOpen(true);
  };

  const handleViewPending = () => {
    if (pendingRequests.length === 1) {
      openDetail(pendingRequests[0].id);
      return;
    }
    setTypeFilter('transferencia');
    historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="container mx-auto p-4">
      {/* Header */}
      <div
        className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        data-guide-id="panel.currentAccount.balance"
      >
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-[-0.6px]">Cuenta Corriente</h1>
          <FieldHelp topicId="panel.currentAccount.balance" />
        </div>
        {(() => {
          const cta = (
            <Button
              onClick={openCreate}
              disabled={!hasDebt || isLoading}
              className="w-full gap-2 sm:w-auto"
            >
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">
                Cancelar deuda por transferencia
              </span>
              <span className="sm:hidden">Cancelar deuda</span>
            </Button>
          );
          return !hasDebt && !isLoading ? (
            <Tooltip
              content="No tenés deuda pendiente"
              className="w-full sm:w-auto"
            >
              {cta}
            </Tooltip>
          ) : (
            cta
          );
        })()}
      </div>

      {/* Balance cards */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Saldo Negativo Actual
              </CardTitle>
              <DollarSignIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-3/4" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Límite de Saldo Negativo
              </CardTitle>
              <DollarSignIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-3/4" />
            </CardContent>
          </Card>
        </div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Saldo Negativo Actual
              </CardTitle>
              <DollarSignIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#DC2626]">
                {currencyFormatter.format(negativeBalance)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Límite de Saldo Negativo
              </CardTitle>
              <DollarSignIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currencyFormatter.format(maxBalance)}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                El saldo negativo no puede superar este límite para crear
                torneos con método de pago en efectivo.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Información */}
      <div className="mt-8">
        <h2 className="mb-4 text-2xl font-bold">
          Información sobre tu Cuenta Corriente
        </h2>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="item-1">
            <AccordionTrigger>¿Qué es la Cuenta Corriente?</AccordionTrigger>
            <AccordionContent>
              <p>
                La Cuenta Corriente de GetMatch te permite gestionar los pagos
                de inscripciones en efectivo de tus torneos. GetMatch lleva un
                registro del "Balance de Cuenta" de tu organización. Cada vez
                que una inscripción se paga en efectivo, el{' '}
                <strong>Fee de GetMatch</strong> se suma como saldo negativo a
                tu balance. Por otro lado, los pagos realizados a través de
                pasarelas de pago (como Mercado Pago) restan de este saldo
                negativo.
              </p>
              <p className="mt-2">
                Los Balances de Cuenta tienen un límite predefinido que no
                puedes superar para crear nuevos torneos.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-2">
            <AccordionTrigger>Composición del Balance</AccordionTrigger>
            <AccordionContent>
              <h3 className="mb-2 font-semibold">
                Pagos de Inscripciones en Efectivo (Saldo Negativo)
              </h3>
              <p>
                Cuando un jugador paga una inscripción en efectivo, se crea un
                registro de pago y el fee de GetMatch se suma a tu saldo
                negativo. Si ese pago se reembolsa (ver Reembolsos), sigue
                asociado al balance pero se ignora al calcular el saldo total.
              </p>
              <h3 className="mb-2 mt-4 font-semibold">
                Pagos de Inscripciones por Gateway (Saldo Positivo)
              </h3>
              <p>
                Cuando un jugador paga una inscripción vía pasarela de pago (ej.
                Mercado Pago), este pago puede contribuir a saldar tu saldo
                negativo. Esto ocurre solo si tenías un saldo negativo
                pendiente. Si el monto a saldar es menor al monto del fee, o si
                el monto a saldar es mayor que el total de la inscripción, solo
                la parte necesaria para saldar la deuda impactará en tu cuenta
                corriente.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-3">
            <AccordionTrigger>Reembolsos</AccordionTrigger>
            <AccordionContent>
              <p>
                Los <strong>reembolsos</strong> están asociados a un pago
                específico y permiten mantener actualizada la Cuenta Corriente
                del organizador. Si un pago es reembolsado, automáticamente se
                excluye ese monto del cálculo del saldo de la Cuenta Corriente.
              </p>
              <h3 className="mb-2 mt-4 font-semibold">
                Reembolsos de Pagos con Mercado Pago
              </h3>
              <p>
                Si la pareja realizó el pago mediante Mercado Pago y se procesa
                un reembolso, la Cuenta Corriente del administrador puede verse
                afectada si el pago original había saldado una deuda. En este
                caso, se debe restablecer la deuda en la cuenta del manager.
              </p>
              <h3 className="mb-2 mt-4 font-semibold">
                Reembolsos de Pagos en Efectivo
              </h3>
              <p>
                Si la pareja se da de baja con más de 48 horas de anticipación
                al inicio del torneo, se te reembolsa el fee de su pago. Si se
                da de baja dentro de las 48 horas previas, el fee no se
                reembolsa.
              </p>
              <p className="mt-2">
                Si eliminás una pareja del torneo, se te reembolsa el fee de su
                pago.
              </p>
              <p className="mt-2">
                Si cancelás el torneo, depende de las parejas completas: si no
                llegó al mínimo, se te reembolsa el fee de todos los pagos en
                efectivo; si ya lo tenía, el fee de los pagos en efectivo o
                cargados a mano queda en tu Cuenta Corriente.
              </p>
              <p className="mt-2">
                El reembolso del fee no mueve plata: la devolución del efectivo
                a los jugadores la coordinás vos.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-4">
            <AccordionTrigger>
              Cancelación de deuda por transferencia
            </AccordionTrigger>
            <AccordionContent>
              <p>
                Si tenés saldo negativo, podés cancelarlo transfiriendo el monto
                y enviándonos el comprobante desde el botón{' '}
                <strong>"Cancelar deuda por transferencia"</strong>. Completás
                el monto, la fecha de la transferencia y adjuntás el
                comprobante.
              </p>
              <p className="mt-2">
                La solicitud queda <strong>en revisión</strong> hasta que
                nuestro equipo la valide (suele tardar hasta 24 hs hábiles). El
                saldo negativo <strong>recién se descuenta cuando aprobamos</strong>{' '}
                la transferencia. Mientras esté en revisión, podés cancelar tu
                propia solicitud.
              </p>
              <p className="mt-2">
                Si rechazamos una solicitud, vas a ver el motivo en el detalle y
                vas a poder reenviarla con las correcciones necesarias.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Banner de transferencias en revisión */}
      {!isLoading && (
        <PendingTransfersBanner
          pending={pendingRequests}
          onView={handleViewPending}
        />
      )}

      {/* Historial */}
      <div ref={historyRef} data-guide-id="panel.currentAccount.movements">
        {!isLoading && !error && (
          <MovementsTable
            movements={movements}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            onSelectTransfer={openDetail}
          />
        )}
      </div>

      <CancelDebtDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={drawerMode}
        request={selectedRequest}
        negativeBalance={negativeBalance}
        onChanged={refetch}
      />
    </div>
  );
};

export default CurrentAccount;
